import cx from 'classnames';
import React, {
    Children,
    cloneElement,
    ComponentPropsWithRef,
    FC,
    forwardRef,
    KeyboardEvent,
    MutableRefObject,
    ReactElement,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';

import Button from '../../../core/button/button';
import { ButtonProps } from '../../../core/button/button';
import { FlosInput } from '../../../core/forms/input/flos-input';
import FlosIcon from '../../../core/icon/flos-icon';

import { DropdownItemProps } from './dropdown-item';
import { useDropdownPosition } from './dropdown-position';

type DropdownProps = {
    /**
     * Label of the dropdown. If size=small => no label will be shown. Use the onChange callback to show the value somewhere.
     **/
    label?: string;
    children?: ReactElement<DropdownItemProps> | ReactElement<DropdownItemProps>[];
    disabled?: boolean;
    /**
     * Selected value. If size=small => no value will be shown.
     **/
    value?: string;
    onChange?: any;
    /**
     * Size of the dropdown - will render dropdown in different size variants. If small  -> no label[selected] will be shown - get it from onChange callback.
     **/
    size?: ButtonProps['size'];
    /**
     * Fixed width of dropdown select in pixels - will cut the label text and add some dots.
     **/
    fixedWidth?: number;
} & Omit<ComponentPropsWithRef<'button'>, 'onChange'>;

export const Dropdown: FC<DropdownProps> = forwardRef<HTMLButtonElement, DropdownProps>(
    ({ label, value, children, onChange, className, size = 'medium', fixedWidth, ...props }: DropdownProps, ref): ReactElement => {
        const inputRef = useRef(ref) as MutableRefObject<HTMLInputElement | null>;
        const listRef = useRef() as MutableRefObject<HTMLDivElement>;
        const buttonRef = useRef() as MutableRefObject<HTMLButtonElement>;
        const selectRef = useRef() as MutableRefObject<HTMLDivElement>;

        const arrayChildren = useMemo(() => Children.toArray(children) as any, [children]);
        const initialOptionIndex = useMemo(() => arrayChildren.findIndex((child: any) => child.props.selected), [arrayChildren]);
        const dropdownPositionCSS = useDropdownPosition(buttonRef, listRef);

        const [isShowOptions, setIsShowOptions] = useState<boolean>(false);
        const [selectedOptionIndex, setSelectedOptionIndex] = useState<number>(initialOptionIndex ?? -1);

        const selectedOption = useMemo(() => {
            const index = value !== undefined ? arrayChildren.findIndex((child: any) => child.props.value === value) : selectedOptionIndex;
            const child = arrayChildren[index];
            const selectedOption = { index, value: child?.props.value ?? value, label: child?.props.children ?? label, iconShape: child?.props.iconShape };

            return selectedOption;
        }, [value, label, arrayChildren, selectedOptionIndex]);

        const toggleOptions = useCallback(() => {
            setIsShowOptions(!isShowOptions);

            // scroll selected into view
            const selected = listRef.current.querySelector('li[aria-selected=true]') as HTMLElement;
            if (selected && !isInParentsViewPort(selected) && typeof selected.scrollIntoView == 'function') {
                setTimeout(function () {
                    selected?.scrollIntoView({
                        behavior: 'smooth',
                        block: 'center',
                        inline: 'center',
                    });
                }, 100);
            }
        }, [isShowOptions]);

        const handleOnSelectOption = useCallback(
            (index: number, isShowOptions: boolean = false) => {
                setSelectedOptionIndex(index);
                setIsShowOptions(isShowOptions);

                if (onChange) {
                    const selectedOption = arrayChildren[index].props;

                    if (inputRef.current) {
                        inputRef.current.value = selectedOption.value;
                    }

                    onChange({
                        target: inputRef.current,
                        currentTarget: inputRef.current,
                        label: selectedOption.children,
                    });
                }
            },
            [arrayChildren, onChange]
        );

        const handleKeyPress = useCallback(
            (index: number) => (e: KeyboardEvent) => {
                switch (e.key) {
                    case ' ':
                    case 'SpaceBar':
                    case 'Enter':
                        e.preventDefault();
                        handleOnSelectOption(index);
                        break;
                    default:
                        break;
                }
            },
            [handleOnSelectOption]
        );

        const handleListKeyDown = useCallback(
            (e: KeyboardEvent) => {
                const currentOptionIndex = selectedOption.index;

                switch (e.key) {
                    case 'Escape':
                        e.preventDefault();
                        setIsShowOptions(false);
                        break;
                    case 'ArrowUp':
                        e.preventDefault();
                        if (currentOptionIndex - 1 >= 0) handleOnSelectOption(currentOptionIndex - 1, true);
                        break;
                    case 'ArrowDown':
                        e.preventDefault();
                        if (currentOptionIndex + 1 < arrayChildren.length) handleOnSelectOption(currentOptionIndex + 1, true);
                        break;
                    default:
                        break;
                }
            },
            [selectedOption, arrayChildren, handleOnSelectOption]
        );

        useEffect(() => {
            const handleClickOutside = (e: MouseEvent) => {
                if (isShowOptions) {
                    if (!listRef.current.contains(e.target as Node) && !buttonRef.current.contains(e.target as Node)) {
                        setIsShowOptions(false);
                    }
                    return;
                }
            };

            document.addEventListener('mousedown', handleClickOutside);

            return () => {
                document.removeEventListener('mousedown', handleClickOutside);
            };
        }, [isShowOptions]);

        return (
            <div className={cx('flos-dropdown', 'flos-dropdown--' + size, props.disabled && 'flos-dropdown--disabled', className)}>
                <div className={'flos-dropdown-select'} ref={selectRef}>
                    <FlosInput autoComplete={'off'} type="hidden" value={selectedOption.value ?? ''} ref={inputRef} />
                    <Button
                        type="button"
                        size={size}
                        aria-haspopup="listbox"
                        aria-expanded={isShowOptions}
                        theme="secondary"
                        onClick={toggleOptions}
                        onKeyDown={handleListKeyDown}
                        ref={buttonRef}
                        iconShape="arrow-down"
                        {...props}
                    >
                        {size !== 'small' ? (
                            selectedOption.iconShape ? (
                                <span className={'u-flex-box u-flex-box--center u-flex-box--gap-xsmall u-flex-box--nowrap'}>
                                    <FlosIcon className={'is-indigo'} shape={selectedOption.iconShape} />
                                    <span
                                        style={fixedWidth ? { width: fixedWidth - 128 } : {}}
                                        className={cx('flos-dropdown-select-label', fixedWidth && 'flos-dropdown-select-label--fixed')}
                                    >
                                        {selectedOption.label}
                                    </span>
                                </span>
                            ) : (
                                <span
                                    style={fixedWidth ? { width: fixedWidth - 96 } : {}}
                                    className={cx('flos-dropdown-select-label', fixedWidth && 'flos-dropdown-select-label--fixed')}
                                >
                                    {selectedOption.label}
                                </span>
                            )
                        ) : null}
                    </Button>
                </div>
                <div className={'flos-dropdown-outer'}>
                    <div
                        ref={listRef}
                        className={cx('flos-dropdown-inner', isShowOptions && 'flos-dropdown-inner--show')}
                        style={{
                            visibility: isShowOptions ? 'visible' : 'hidden',
                            ...dropdownPositionCSS,
                        }}
                    >
                        <ul role={'listbox'} className={cx('flos-dropdown-list', 'u-scrollbar')}>
                            {Children.map(children, (child: any, index: number) => {
                                return cloneElement(child as any, {
                                    key: 'dropdown-item-' + index,
                                    selected: selectedOption.index == index,
                                    onClick: () => handleOnSelectOption(index),
                                    onKeyPress: handleKeyPress(index),
                                });
                            })}
                        </ul>
                    </div>
                </div>
            </div>
        );
    }
);

Dropdown.displayName = 'Dropdown';

const isInParentsViewPort = (element: HTMLElement) => {
    const parent = element?.offsetParent;
    if (parent) {
        const elemBounding = element.getBoundingClientRect();
        const parentBounding = parent.getBoundingClientRect();
        return elemBounding.bottom <= parentBounding.height + parentBounding.top && elemBounding.top >= parentBounding.top;
    }
    return false;
};

export type { DropdownProps, DropdownItemProps };
export default Dropdown;
